import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Brain, Home, Users, Activity, LogOut, Contrast, Type, Volume2, VolumeX, Settings2, Menu,
  Flame, FlaskConical,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useState } from "react";

const NAV_PATIENT = [
  { to: "/today", label: "Hoy", icon: Home, id: "nav-today" },
  { to: "/dashboard", label: "Mi perfil", icon: Home, id: "nav-dashboard" },
  { to: "/history", label: "Historial", icon: Flame, id: "nav-history" },
  { to: "/sessions", label: "Sesiones", icon: Activity, id: "nav-sessions" },
  { to: "/assessment/pre", label: "Evaluación PRE", icon: Brain, id: "nav-pre" },
  { to: "/assessment/post", label: "Evaluación POST", icon: Brain, id: "nav-post" },
  { to: "/taxonomy", label: "Áreas y errores", icon: Brain, id: "nav-taxonomy" },
];

const NAV_PRO = [
  { to: "/pro", label: "Panel profesional", icon: Users, id: "nav-pro" },
  { to: "/research", label: "Investigación", icon: FlaskConical, id: "nav-research" },
  { to: "/taxonomy", label: "Áreas y errores", icon: Brain, id: "nav-pro-taxonomy" },
];

const NAV_ADMIN = [
  { to: "/admin/users", label: "Autorizar accesos", icon: Users, id: "nav-admin-users" },
  { to: "/admin/invitations", label: "Invitaciones", icon: Activity, id: "nav-admin-invitations" },
  { to: "/admin/audit", label: "Auditoría", icon: Activity, id: "nav-admin-audit" },
  { to: "/research", label: "Investigación", icon: FlaskConical, id: "nav-admin-research" },
  { to: "/pro", label: "Panel profesional", icon: Users, id: "nav-admin-pro" },
  { to: "/taxonomy", label: "Áreas y errores", icon: Brain, id: "nav-admin-taxonomy" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { mode, cycle, voiceAssist, setVoiceAssist, largeText, setLargeText } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const isPro = user?.role === "professional";
  const nav = isAdmin ? NAV_ADMIN : isPro ? NAV_PRO : NAV_PATIENT;

  const NavLinks = () => (
    <nav className="flex flex-col gap-1">
      {nav.map((n) => {
        const Active = location.pathname.startsWith(n.to);
        const Icon = n.icon;
        return (
          <Link
            key={n.to}
            to={n.to}
            data-testid={n.id}
            onClick={() => setOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              Active
                ? "bg-primary text-primary-foreground"
                : "hover:bg-secondary text-foreground/80"
            }`}
          >
            <Icon size={20} />
            <span className="font-medium">{n.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background text-foreground grain">
      {/* Top bar */}
      <header className="border-b border-border/60 bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="open-menu-btn">
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <div className="mt-6"><NavLinks /></div>
              </SheetContent>
            </Sheet>
          </div>
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <Logo size="sm" testid="appshell-brand-logo" className="hidden sm:block" />
            <div className="sm:hidden w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center">
              <Brain size={20} />
            </div>
            <div className="leading-tight sm:hidden">
              <div className="font-extrabold heading text-base tracking-tight">
                SocialCog<span className="text-primary">®</span>
              </div>
            </div>
          </Link>

          <div className="flex-1" />

          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="ghost" size="icon" title="Modo visual" data-testid="theme-toggle"
              onClick={cycle}
            ><Contrast size={18} /></Button>
            <Button
              variant="ghost" size="icon" title="Texto grande" data-testid="text-toggle"
              onClick={() => setLargeText(!largeText)}
              aria-pressed={largeText}
            ><Type size={18} /></Button>
            <Button
              variant="ghost" size="icon" title="Narración por voz" data-testid="voice-toggle"
              onClick={() => setVoiceAssist(!voiceAssist)}
              aria-pressed={voiceAssist}
            >{voiceAssist ? <Volume2 size={18} /> : <VolumeX size={18} />}</Button>
            <span className="text-xs px-2 py-1 rounded-md bg-secondary text-secondary-foreground uppercase tracking-wide font-semibold" data-testid="theme-label">
              {mode}
            </span>
          </div>

          {user && (
            <div className="flex items-center gap-2 ml-2">
              <Link to="/settings" data-testid="nav-settings" className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-secondary">
                <Settings2 size={16} />
                <span className="text-sm">{user.name}</span>
              </Link>
              <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
                <LogOut size={16} /> <span className="ml-1 hidden sm:inline">Salir</span>
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <NavLinks />
            <div className="mt-6 border-t border-border pt-6 text-xs text-muted-foreground leading-relaxed">
              <p className="uppercase tracking-wide font-semibold mb-1">Sesión activa</p>
              <p>{user?.role === "patient" ? "Paciente" : user?.role}</p>
              <p className="mt-1">{user?.email}</p>
            </div>
          </div>
        </aside>
        <main>{children}</main>
      </div>
    </div>
  );
}
